import React, { useState } from 'react';
import inventoryService from '../../services/inventoryService';
import { useStore } from '../../context/StoreContext';
import { toLocalDateTimeString } from '../../utils/dateUtils';

const HandheldReceivingPage = () => {
  const { activeStore } = useStore();
  const [poNumber, setPoNumber] = useState('');
  const [lookup, setLookup] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [receivedQty, setReceivedQty] = useState('');
  const [notes, setNotes] = useState('');
  const [receipts, setReceipts] = useState([]);
  const [status, setStatus] = useState({ type: '', message: '' });

  const lookupProduct = async () => {
    if (!activeStore?._id) {
      setStatus({ type: 'error', message: 'Select store to receive stock.' });
      return;
    }
    if (!lookup.trim()) {
      setStatus({ type: 'error', message: 'Scan or enter a barcode/SKU.' });
      return;
    }
    try {
      const results = await inventoryService.searchProducts(activeStore._id, lookup.trim());
      const product =
        results.find(
          (p) =>
            p.barcode?.toLowerCase() === lookup.trim().toLowerCase() ||
            p.sku?.toLowerCase() === lookup.trim().toLowerCase()
        ) || results[0];
      if (!product) {
        setCurrentProduct(null);
        setStatus({ type: 'error', message: 'Product not found.' });
        return;
      }
      setCurrentProduct(product);
      setStatus({ type: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: 'Unable to load product.' });
    }
  };

  const handleReceive = async () => {
    if (!activeStore?._id || !currentProduct) return;
    const quantity = Number(receivedQty);
    if (Number.isNaN(quantity) || quantity <= 0) {
      setStatus({ type: 'error', message: 'Enter a positive quantity.' });
      return;
    }
    const poLabel = poNumber ? `PO ${poNumber}` : 'Unscheduled receipt';
    const newQuantity = (currentProduct.stock || 0) + quantity;
    try {
      await inventoryService.adjustStock(currentProduct._id, {
        storeId: activeStore._id,
        newQuantity,
        reason: `${poLabel} - ${notes || 'Goods received'}`,
      });
      const entry = {
        productName: currentProduct.name,
        poLabel,
        quantity,
        timestamp: new Date().toISOString(),
      };
      setReceipts((prev) => [entry, ...prev]);
      setCurrentProduct({ ...currentProduct, stock: newQuantity });
      setReceivedQty('');
      setLookup('');
      setNotes('');
      setStatus({ type: 'success', message: 'Stock received and updated.' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: err.response?.data?.message || 'Failed to receive stock.' });
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Purchase Order #</label>
          <input
            type="text"
            value={poNumber}
            onChange={(e) => setPoNumber(e.target.value)}
            placeholder="Optional reference"
            className="w-full border border-gray-300 rounded-xl px-3 py-2 mt-1"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Scan item</label>
          <div className="flex gap-2 mt-1">
            <input
              type="text"
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
              placeholder="Barcode / SKU / search term"
            />
            <button
              type="button"
              onClick={lookupProduct}
              className="px-4 py-2 rounded-xl bg-slate-900 text-white"
            >
              Lookup
            </button>
          </div>
        </div>
        {status.message && (
          <p className={status.type === 'error' ? 'text-sm text-red-600' : 'text-sm text-green-600'}>
            {status.message}
          </p>
        )}
      </div>

      {currentProduct && (
        <div className="bg-white rounded-2xl shadow p-4 space-y-3">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-slate-400">Receiving</p>
            <h3 className="text-xl font-semibold">{currentProduct.name}</h3>
            <p className="text-sm text-slate-500">Recorded stock: {currentProduct.stock ?? 0}</p>
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700">Quantity received</label>
            <input
              type="number"
              value={receivedQty}
              onChange={(e) => setReceivedQty(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
              min="1"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700">Notes</label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Carton, damages, etc."
              className="w-full border border-gray-300 rounded-xl px-3 py-2"
            />
          </div>
          <button
            type="button"
            onClick={handleReceive}
            className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600"
          >
            Confirm receipt
          </button>
        </div>
      )}

      {receipts.length > 0 && (
        <div className="bg-white rounded-2xl shadow p-4">
          <p className="text-xs uppercase tracking-[0.4em] text-slate-400 mb-2">Recent receipts</p>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {receipts.map((entry, idx) => (
              <div key={`${entry.productName}-${idx}`} className="border rounded-xl p-3">
                <p className="font-semibold">{entry.productName}</p>
                <p className="text-sm text-slate-500">
                  {entry.quantity} units • {entry.poLabel}
                </p>
                <p className="text-xs text-slate-400">
                  {toLocalDateTimeString(entry.timestamp)}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default HandheldReceivingPage;
